import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Apple, Download } from 'lucide-react';
import phoneOne from '@/assets/phone-1.png';
import phoneTwo from '@/assets/phone-2.png';

export function DownloadSection() {
  const { t } = useLanguage();
  const downloadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animated-element');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('appear');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (downloadRef.current) {
      observer.observe(downloadRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={downloadRef} id="download" className="py-12 md:py-20">
      <div className="container px-4 md:px-6">
        <div className="bg-card rounded-[2rem] border border-border overflow-hidden shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 items-center">
            <div className="p-8 md:p-12 lg:p-16 animated-element">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-6">
                {t('home.download.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-md leading-relaxed">
                {t('home.download.subtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="rounded-xl px-6 py-6 font-medium"
                  asChild
                >
                  <a href="https://apps.apple.com/us/app/sportlogin/id6443801938" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3">
                    <Apple className="w-6 h-6" />
                    <div className="flex flex-col items-start">
                      <span className="text-[10px] uppercase opacity-80 leading-none">{t('home.download.ios')}</span>
                      <span className="font-semibold leading-tight">App Store</span>
                    </div>
                  </a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-xl px-6 py-6 font-medium border-2 border-foreground hover:bg-secondary"
                  asChild
                >
                  <a href="https://play.google.com/store/apps/details?id=com.thepoppyapp.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3">
                    <Download className="w-6 h-6" />
                    <div className="flex flex-col items-start">
                      <span className="text-[10px] uppercase opacity-80 leading-none">{t('home.download.android')}</span>
                      <span className="font-semibold leading-tight">Google Play</span>
                    </div>
                  </a>
                </Button>
              </div>
            </div>

            <div className="relative flex justify-center items-center h-full min-h-[360px] md:min-h-[460px] bg-secondary/50 p-8 animated-element">
              <div className="absolute w-56 h-56 rounded-full bg-accent/20 blur-3xl" />
              <div className="relative flex items-center justify-center gap-2 md:gap-4">
                <img
                  src={phoneOne}
                  alt="ThePoppyApp mobile calendar screen"
                  loading="lazy"
                  width={700}
                  height={1200}
                  className="w-32 md:w-40 lg:w-44 object-contain drop-shadow-2xl -rotate-6"
                />
                <img
                  src={phoneTwo}
                  alt="ThePoppyApp mobile student list screen"
                  loading="lazy"
                  width={700}
                  height={1200}
                  className="w-32 md:w-40 lg:w-44 object-contain drop-shadow-2xl rotate-6"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
